import Modal from "react-responsive-modal";
import { ReactComponent as CloseIcon } from "assets/img/icons/cancel.svg";
import { ReactComponent as AssistantIcon } from "assets/img/icons/AI-assistant-colour.svg";
import { useUserContext } from "store/contexts/userContext";
import LazyLoadImage from "components/LazyLoadImage";
import { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useToasts } from "react-toast-notifications";
import TeamService from "services/TeamService";
import Loader from "components/Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faCopy } from "@fortawesome/free-solid-svg-icons";
import CustomTextArea from "components/CustomTextArea";
import copy from "copy-to-clipboard";

const GeneratedNotesResponse = ({ defaultQuery, handleModal }) => {
  const { user } = useUserContext();
  const [conversation, setConversation] = useState([]);
  const { addToast } = useToasts();
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      query: defaultQuery,
    },
    validationSchema: Yup.object().shape({
      query: Yup.string()
        .min(50, "Query must be 50 characters.")
        .max(5000, "Query can not be greater than 5000 characters.")
        .trim()
        .required("Please enter the value."),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      setConversation((prev) => [...prev, { type: 1, text: values.query }]);
      fetchResponse(values.query);
      formik.setFieldValue("query", "");
    },
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  const fetchResponse = async (query) => {
    try {
      setLoading(true);
      const response = await TeamService.getAiTutorResp({
        query: query,
        conversationId,
      });

      setConversationId(response?.data?.data?.conversationId ?? null);
      setConversation((prev) => [
        ...prev,
        { type: 2, text: response?.data?.data?.response ?? "" },
      ]);
      setLoading(false);
    } catch {
      setLoading(false);
      addToast("Some error occurred.", {
        appearance: "error",
      });
    }
  };

  useEffect(() => {
    formik.handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyText = async () => {
    var text;
    conversation.forEach((item) => {
      let who = item.type === 1 ? user.first_name : "Ai Assistant";
      let who_with_content = who + ": " + item.text;
      text = text ? text + "\n" + who_with_content : who_with_content;
    });

    copy(text);
    addToast("conversation is copied", {
      appearance: "success",
    });
  };

  return (
    <Modal
      open
      onClose={handleModal}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      showCloseIcon={false}
      center={true}
      classNames={{
        modalContainer: "modalContainer",
        modal: "createTaskModal",
      }}
    >
      <div
        className="generated-names-response-root customScrollArea"
        style={{ maxHeight: "90vh", overflowY: "auto" }}
      >
        <div className="response-header">
          <div className="heading">
            Welcome to ReTeam Tutor, a conversational assistant that helps
            clarify idea with thought provoking questions
          </div>
          <CloseIcon onClick={handleModal} className="closeIcon" />
        </div>
        <div className="copy-conversation">
          <span>Copy the conversation</span>
          <span>
            {" "}
            <FontAwesomeIcon
              className="copy-icon"
              icon={faCopy}
              onClick={copyText}
            />
          </span>
        </div>

        <div
          className="response-container customScrollArea"
          style={{ minHeight: "400px", maxHeight: "450px", overflowY: "auto" }}
        >
          {conversation.map((c, i) => {
            if (c.type === 1) {
              return <UserQuery text={c.text} key={i} user={user} />;
            }

            return <AssistanceResponse text={c.text} key={i} />;
          })}
          <div ref={messagesEndRef} />
        </div>
        <form noValidate onSubmit={formik.handleSubmit}>
          <div className="write-query-root">
            <div className="input-root">
              <CustomTextArea
                allowEnterKey
                disabled={loading}
                formik={formik}
                placeholder={"Ask more"}
                fieldName={"query"}
                rows={2}
              />
            </div>
            <button
              type="submit"
              className="createCommentButton"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>
        </form>

        {loading && (
          <div className="loader-root">
            <Loader height={50} width={50} />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default GeneratedNotesResponse;

const UserQuery = ({ text, user }) => (
  <div className="user-info">
    <div className="assistance-username">
      <LazyLoadImage image={user?.avatar} imageClassName="userAvatar" />
      {`${user.first_name} ${user.last_name}`}
    </div>
    <div
      className="customScrollArea"
      style={{
        maxHeight: "80px",
        fontSize: "15px",
        overflowY: "auto",
        whiteSpace: "pre-wrap",
      }}
    >
      <div>{text}</div>
    </div>
  </div>
);

const AssistanceResponse = ({ text }) => (
  <div className="mt-3">
    <div className="assistance-heading">
      <AssistantIcon width={50} />
      Assistant
    </div>
    <div
      className="customScrollArea"
      style={{
        maxHeight: "80px",
        fontSize: "15px",
        overflowY: "auto",
        whiteSpace: "pre-wrap",
      }}
    >
      {text}
    </div>
  </div>
);
